import { config } from 'config/config';
import { ISalesforceCenter, IEditPharmacy } from 'src/shared/models';

export interface IRegisterStep {
  content: JSX.Element;
  id: RegisterStepsEnum;
  title: string;
}

export interface IRegisterHandler {
  stateList: {
    available_regions: { id: number; name: string; code: string }[];
  };
}

export enum RegisterStepsEnum {
  ACCOUNT_DATA = 0,
  PERSONAL_DATA = 1,
  PRACTICE_DATA = 2,
  PRACTICE_DATA_USA = 3,
}

export interface IPracticeForm {
  city: string;
  contactRepSale: boolean;
  directionNumber: string;
  directionStreet: string;
  name: string;
  state: string;
  telephone: string;
  zipCode: string;
}

export interface IPracticeData {
  canContinue: boolean;
  contactRepSale: boolean;
  idCenter?: string;
  idSalesforce?: string;
  isCenterRegistered: boolean;
  isPracticeRegistered: boolean;
  practice: IPracticeForm;
  practiceName: string;
  practiceType: 'Farmacia' | 'Centro_Medico';
  saleforceCenter: ISalesforceCenter;
  userIsOwnerManager: boolean;
  zipCode: string;
}

export type IPracticeDataUSA = Omit<
  IPracticeData,
  'isPracticeRegistered' | 'isCenterRegistered' | 'practice'
>;

export interface ISignInData {
  embracePractices: ISalesforceCenter[];
  LIWWPractices: ISalesforceCenter[];
  USAPractices: ISalesforceCenter[];
}

export interface IPersonalData {
  birthday: string;
  gender: string;
  canContinue: boolean;
  clientType: 'Staff' | 'HCP';
  idMagentoSpecialty: number;
  idSalesforceSpecialty: number;
  idSpecialty: number;
  name: string;
  npi: string;
  surname: string;
  telephone: string;
  userType: 'Employee' | 'Manager';
  newsletter: boolean;
  terms_conditions: boolean;
}

export interface IAccountData {
  canContinue: boolean;
  email: string;
  emailRepeat: string;
  isValidPassword: boolean;
  newsletter: boolean | null;
  password: string;
  terms_conditions: boolean;
}

export interface IConAccountData {
  canContinue: boolean;
  email: string;
  isValidPassword: boolean;
  password: string;
  birthday: string;
  gender: string;
  name: string;
  surname: string;
  telephone: string;
  newsletter: boolean;
  terms: boolean;
}

export interface IPregnancyModel {
  acceptedDate: string;
  termsAccepted: boolean;
  babyName?: string;
}

export function buildCenterData(data: IPracticeData, isUserManager: boolean) {
  if (data.isPracticeRegistered || data.isCenterRegistered) {
    const { saleforceID, centerID, centerName } = data.saleforceCenter;

    return isUserManager
      ? {
          idSalesforce: saleforceID,
          centerName,
          centerType: config.REGISTER.PRACTICE_TYPE,
        }
      : { uuidCenter: centerID };
  }

  return {
    // Center does not exist
    centerDirectionCity: data.practice.city,
    centerDirectionCountry: config.REGISTER.DIRECTION_COUNTRY,
    centerDirectionCP: data.practice.zipCode,
    centerDirectionNumber: data.practice.directionNumber,
    centerDirectionState: data.practice.state,
    centerDirectionStreet: data.practice.directionStreet,
    centerName: data.practice.name,
    centerPhone: data.practice.telephone,
    centerType: config.REGISTER.PRACTICE_TYPE,
    CIF: '',
    PlatformID: config.REGISTER.PLATFORM_ID,
  };
}

export function buildCenterDataUSA(
  data: IPracticeDataUSA,
  isUserManager: boolean,
) {
  const { saleforceID, centerID, centerName } = data.saleforceCenter;

  return isUserManager
    ? {
        idSalesforce: saleforceID,
        centerName,
        centerType: config.REGISTER.PRACTICE_TYPE,
      }
    : { uuidCenter: centerID };
}

export function buildUserData(
  practiceData: IPracticeData,
  personalData: IPersonalData,
  accountData: IAccountData,
) {
  return {
    PlatformID: config.REGISTER.PLATFORM_ID,

    idLanguage: config.REGISTER.ID_LANGUAGE,
    idCountry: config.REGISTER.ID_COUNTRY,
    behavior: config.REGISTER.BEHAVIOR,

    name: personalData.name,
    lastName: personalData.surname,
    surName: personalData.surname,
    userType: personalData.userType,
    newsletterSubscription: String(Number(accountData.newsletter)),
    idSpecialty: personalData.idSpecialty,
    idMagentoSpecialty: personalData.idMagentoSpecialty,
    idSalesforceSpecialty: personalData.idSalesforceSpecialty,
    clientType: personalData.clientType,
    npi: personalData.npi,
    telephone: personalData.telephone,
    centerDirectionCP:
      practiceData.practice?.zipCode ||
      practiceData.saleforceCenter?.centerDirectionCP,

    email: accountData.email,
    password: accountData.password,
    repVisit: practiceData.contactRepSale,
  };
}

export const initialPracticeData: IPracticeData = {
  canContinue: false,
  contactRepSale: null,
  isCenterRegistered: null,
  isPracticeRegistered: null,
  practice: null,
  practiceName: '',
  practiceType: 'Farmacia',
  saleforceCenter: null,
  userIsOwnerManager: null,
  zipCode: null,
};

export const initialPracticeDataUSA: IPracticeDataUSA = {
  canContinue: false,
  contactRepSale: null,
  practiceName: '',
  practiceType: 'Farmacia',
  saleforceCenter: null,
  zipCode: null,
  userIsOwnerManager: null,
};

export const initialPersonalData: IPersonalData = {
  canContinue: false,
  clientType: null,
  idMagentoSpecialty: -1,
  idSalesforceSpecialty: -1,
  idSpecialty: -1,
  name: '',
  gender: '',
  birthday: '',
  npi: '',
  surname: '',
  telephone: '',
  userType: null,
  newsletter: null,
  terms_conditions: false,
};

export const initialAccountData: IAccountData = {
  canContinue: false,
  email: '',
  emailRepeat: '',
  isValidPassword: false,
  newsletter: false,
  password: '',
  terms_conditions: false,
};

export const initialPharmacytData: IEditPharmacy = {
  accountName: '',
  city: '',
  id: '',
  regionName: '',
  street: '',
  streetNumber: '',
  zipcode: '',
  latitude: '',
  longitude: '',
};

export const initialConAccountData: IConAccountData = {
  canContinue: false,
  email: '',
  isValidPassword: false,
  password: '',
  birthday: '',
  gender: '',
  name: '',
  surname: '',
  telephone: '',
  newsletter: null,
  terms: false,
};
